import type { ReactNode } from 'react';
import { scopeLabel } from '@aithora/shared';
import { useFacilityScope } from '../session/FacilityScope';
import { sx } from './designStyles';

/**
 * The facility switcher picked a place with nothing on this page (design geoLayer, the
 * "Nothing in this view" card): the place, what is missing, and Show all facilities. Never a
 * page of zeros. `title` is the design's line, worded with the place: "Nothing from {place}
 * here yet" or "No facilities in {place} report here yet".
 */
export function ScopeEmpty({ title, body, more }: { title: (place: string) => string; body: string; more?: ReactNode }) {
  const { scope, setScope, inView } = useFacilityScope();
  const place = scopeLabel(scope);
  const label = place + (scope.fac ? '' : ` · ${inView.length}`);
  return (
    <div role="status" data-screen-label="Nothing in this view" data-testid="scope-empty"
      style={sx('box-sizing:border-box;max-width:640px;width:100%;margin:40px auto;padding:28px;border-radius:12px;border:1px solid rgba(34,39,46,.14);background:#FCFAF4;display:flex;flex-direction:column;gap:12px;')}>
      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>{label}</span>
      <span style={sx('font-size:22px;font-weight:800;letter-spacing:-.015em;color:#22272E;text-wrap:pretty;')}>{title(place)}</span>
      <span style={sx('font-size:15px;line-height:1.5;color:#3C454F;text-wrap:pretty;')}>{body}</span>
      {more}
      <div style={sx('display:flex;flex-wrap:wrap;gap:8px;margin-top:4px;')}>
        <button type="button" onClick={() => setScope({})}
          style={sx('display:inline-flex;align-items:center;min-height:40px;box-sizing:border-box;padding:8px 16px;line-height:1.3;white-space:nowrap;border-radius:9px;background:#2F5C97;border:0;font-family:inherit;font-size:14px;font-weight:700;color:#FFFFFF;cursor:pointer;')}>
          Show all facilities
        </button>
      </div>
    </div>
  );
}

/** "Nothing from {place} here yet" (design: Guardian, Worklist, Atlas). */
export const nothingFrom = (place: string) => `Nothing from ${place} here yet`;
/** "No facilities in {place} report here yet" (design: Overview, Performance). */
export const noFacilitiesIn = (place: string) => `No facilities in ${place} report here yet`;
